import { __ } from '@wordpress/i18n';
import {
	useBlockProps,
	InspectorControls,
	MediaUpload,
	MediaUploadCheck,
	PanelColorSettings,
} from '@wordpress/block-editor';

import {
	PanelBody,
	SelectControl,
	RangeControl,
	Button,
	TextControl,
	ToggleControl,
} from '@wordpress/components';

import './editor.scss';

export default function Edit({ attributes, setAttributes }) {

	const {
		logos,
		template,
		animationDuration,
		showAltText,
		logoSize,
		slideDirection,
		displayMode,
		wrapperBgColor,
		itemBgColor,
		itemBorderColor,
		itemBorderWidth,
		itemBorderRadius,
		altTextColor,
	} = attributes;

	const blockProps = useBlockProps();
	const isPro = true;

	// ── Logo CRUD ───────────────────────────────────────────────────
	const addLogo = (media) => {
		setAttributes({
			logos: [...logos, { id: media.id, url: media.url, alt: media.alt || '', link: '' }]
		});
	};

	const updateLogo = (index, field, value) => {
		const updated = [...logos];
		updated[index][field] = value;
		setAttributes({ logos: updated });
	};

	const removeLogo = (index) => {
		setAttributes({ logos: logos.filter((_, i) => i !== index) });
	};

	// ── Links ───────────────────────────────────────────────────────
	const upgradeLink = 'https://checkout.freemius.com/plugin/24598/plan/40853/licenses/1/';
	const rateLink    = 'https://wordpress.org/support/plugin/wbd-logo-carousel/reviews/#new-post';
	const pageLink    = 'https://www.wpbranddigital.org/contact/';
	const demoLink    = 'https://demo.wpbranddigital.org/logo-carousel-demo/';

	const isProTemplate = (v) => ['luxury','modern','minimal','premium','neon','glassmorphism'].includes(v);
	const isProDisplay  = (v) => ['grid','masonry','multirow'].includes(v);

	// ── Inline styles ───────────────────────────────────────────────
	const itemStyle = (() => {
		const s = {};
		if (itemBgColor)     s.background  = itemBgColor;
		if (itemBorderColor) s.borderColor = itemBorderColor;
		if (itemBorderWidth) { s.borderWidth = `${itemBorderWidth}px`; s.borderStyle = 'solid'; }
		if (itemBorderRadius !== undefined && itemBorderRadius !== null)
			s.borderRadius = `${itemBorderRadius}px`;
		return s;
	})();

	const wrapperStyle = (extra = {}) => ({
		...(wrapperBgColor ? { background: wrapperBgColor } : {}),
		...extra,
	});

	const altStyle = altTextColor ? { color: altTextColor } : {};

	// ── Tooltip position diagram labels ────────────────────────────
	const positionLabels = { top: '↑ Top', bottom: '↓ Bottom', left: '← Left', right: '→ Right' };

	// ── Split logos for multirow ────────────────────────────────────
	const splitIntoRows = (arr, rowCount) => {
		if (!arr.length) return Array.from({ length: rowCount }, () => []);
		const rows = Array.from({ length: rowCount }, () => []);
		arr.forEach((item, i) => rows[i % rowCount].push(item));
		return rows;
	};

	// ── Logo item renderer (editor preview) ────────────────────────
	// Note: in editor we render tooltip as static visible for preview;
	// on frontend it is controlled by JS + fixed positioning.
	const renderItem = (logo, index, cls) => {
		return (
			<div
				key={index}
				className={cls}
				style={itemStyle}
			>
				{logo.link ? (
					<a href={logo.link}>
						<img src={logo.url} alt={logo.alt} style={{ maxWidth: `${logoSize}px` }} />
						{showAltText && logo.alt && (
							<span className="logo-carousel__alt-text" style={altStyle}>{logo.alt}</span>
						)}
					</a>
				) : (
					<>
						<img src={logo.url} alt={logo.alt} style={{ maxWidth: `${logoSize}px` }} />
						{showAltText && logo.alt && (
							<span className="logo-carousel__alt-text" style={altStyle}>{logo.alt}</span>
						)}
					</>
				)}
			</div>
		);
	};

	const EmptyNotice = () => (
		<div className="wlcs-add-logo-section">
			<p>⚠️ No logos added. Please add logos from the sidebar.</p>
		</div>
	);

	const getRepeatedLogos = (logosList) => {
		if (!logosList || logosList.length === 0) return [];
		let base = [...logosList];
		while (base.length < 8) {
			base = base.concat(logosList);
		}
		return base.concat(base);
	};

	// ── Layout previews ─────────────────────────────────────────────
	const renderCarouselPreview = () => (
		<div
			className={`logo-carousel logo-carousel--${template} logo-carousel--${slideDirection}`}
			style={wrapperStyle({ '--animation-duration': `${animationDuration}s` })}
		>
			{logos.length === 0 && <EmptyNotice />}
			<div className="logo-carousel__track">
				{getRepeatedLogos(logos).map((logo, i) => renderItem(logo, i, 'logo-carousel__item'))}
			</div>
		</div>
	);



	// ── Sidebar ─────────────────────────────────────────────────────
	return (
		<>
			<InspectorControls>

				{/* HELP & SUPPORT */}
				<PanelBody title={__('Help & Support', 'wbd-logo-carousel')}>
					<p style={{ marginBottom: '10px' }}>{__('If you enjoy using this plugin, please consider rating it or contact us for help!', 'wbd-logo-carousel')}</p>
					<div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
						<Button isPrimary onClick={() => window.open(rateLink, '_blank')}>{__('Rate Plugin', 'wbd-logo-carousel')}</Button>
						<Button isPrimary onClick={() => window.open(pageLink, '_blank')}>{__('Contact Support', 'wbd-logo-carousel')}</Button>
					</div>
				</PanelBody>

				{/* THEME */}
				<PanelBody title={__('Theme Style', 'wbd-logo-carousel')} initialOpen={true}>
					<SelectControl
						label={__('Theme Style', 'wbd-logo-carousel')}
						value={template}
						options={[
							{ label: __('Classic', 'wbd-logo-carousel'),       value: 'classic'       },
							{ label: __('Elegant', 'wbd-logo-carousel'),       value: 'elegant'       },
						]}
						onChange={(value) => setAttributes({ template: value })}
					/>
				</PanelBody>

				{/* LOGO MANAGEMENT */}
				<PanelBody title="Manage Logos" initialOpen={true}>
					{logos.map((logo, index) => (
						<div key={index} className="logo-sidebar-card">
							<img className="logo-sidebar-image" src={logo.url} alt={logo.alt || ''} />
							<TextControl
								label="Link URL"
								value={logo.link}
								onChange={(value) => updateLogo(index, 'link', value)}
								placeholder="https://"
							/>
							<TextControl
								label="Alt Text"
								value={logo.alt}
								onChange={(value) => updateLogo(index, 'alt', value)}
							/>

							<Button isDestructive onClick={() => removeLogo(index)}>Remove Logo</Button>
						</div>
					))}
					<MediaUploadCheck>
						<MediaUpload
							onSelect={addLogo}
							allowedTypes={['image']}
							render={({ open }) => (
								<Button
									onClick={open} variant="primary"
									style={{ marginTop: '10px', width: '100%', display: 'flex', justifyContent: 'center' }}
								>
									+ Add Logo
								</Button>
							)}
						/>
					</MediaUploadCheck>
				</PanelBody>

				{/* CAROUSEL SETTINGS */}
				{displayMode === 'carousel' && (
					<PanelBody title="Carousel Settings">
						<SelectControl
							label="Slide Direction"
							value={slideDirection}
							options={[
								{ label: 'Left',  value: 'left'  },
								{ label: 'Right', value: 'right' },
							]}
							onChange={(value) => setAttributes({ slideDirection: value })}
						/>
						<RangeControl
							label="Animation Duration (seconds)"
							value={animationDuration}
							onChange={(value) => setAttributes({ animationDuration: value })}
							min={10} max={60} step={5}
						/>
					</PanelBody>
				)}



				{/* LOGO SETTINGS */}
				<PanelBody title="Logo Settings">
					<RangeControl
						label="Logo Size (px)" value={logoSize}
						onChange={(value) => setAttributes({ logoSize: value })}
						min={50} max={300} step={10}
					/>
					<ToggleControl
						label="Show Alt Text"
						checked={showAltText}
						onChange={(value) => setAttributes({ showAltText: value })}
					/>


				</PanelBody>

				{/* COLOR SETTINGS */}
				<PanelColorSettings
					title={__('Color Settings', 'wbd-logo-carousel')}
					initialOpen={false}
					colorSettings={[
						{ label: __('Wrapper Background', 'wbd-logo-carousel'), value: wrapperBgColor, onChange: (c) => setAttributes({ wrapperBgColor: c || '' }) },
						{ label: __('Card Background', 'wbd-logo-carousel'),    value: itemBgColor,    onChange: (c) => setAttributes({ itemBgColor: c || '' }) },
						{ label: __('Card Border Color', 'wbd-logo-carousel'),  value: itemBorderColor, onChange: (c) => setAttributes({ itemBorderColor: c || '' }) },
						{ label: __('Alt Text Color', 'wbd-logo-carousel'),     value: altTextColor,   onChange: (c) => setAttributes({ altTextColor: c || '' }) },
					]}
				/>

				{/* BORDER & RADIUS */}
				<PanelBody title={__('Border & Radius', 'wbd-logo-carousel')} initialOpen={false}>
					<RangeControl
						label={__('Border Width (px)', 'wbd-logo-carousel')}
						value={itemBorderWidth}
						onChange={(value) => setAttributes({ itemBorderWidth: value })}
						min={0} max={10} step={1} allowReset resetFallbackValue={0}
					/>
					<RangeControl
						label={__('Border Radius (px)', 'wbd-logo-carousel')}
						value={itemBorderRadius}
						onChange={(value) => setAttributes({ itemBorderRadius: value })}
						min={0} max={60} step={1} allowReset resetFallbackValue={8}
					/>
					<div style={{ marginTop: '16px' }}>
						<p style={{ fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', color: '#757575', marginBottom: '8px' }}>Live Preview</p>
						<div style={{
							height: '60px',
							background:   itemBgColor || '#ffffff',
							border:       `${itemBorderWidth ?? 1}px solid ${itemBorderColor || '#e0e0e0'}`,
							borderRadius: `${itemBorderRadius ?? 8}px`,
							display: 'flex', alignItems: 'center', justifyContent: 'center',
							fontSize: '12px', color: '#999', transition: 'all 0.2s ease',
						}}>Card preview</div>
					</div>
				</PanelBody>

				{/* PRO FEATURES */}
				<PanelBody title={__('Pro Features', 'wbd-logo-carousel')} initialOpen={true}>
					<div className="wbd-pro-features-container">
						<h3 className="wbd-pro-features-title">🔥 Pro Features</h3>
						<p className="wbd-pro-features-subtitle">
							{__('Upgrade to ', 'wbd-logo-carousel')}<strong>{__('Logo Carousel Pro', 'wbd-logo-carousel')}</strong>{__(' to unlock:', 'wbd-logo-carousel')}
						</p>
						<ul className="wbd-pro-features-list">
							<li>
								<span className="wbd-pro-feature-icon">✨</span>
								<span>
									<strong>{__('6 Premium Templates', 'wbd-logo-carousel')}</strong> — Luxury, Modern, Minimal, Premium, Neon, Glassmorphism
								</span>
							</li>
							<li>
								<span className="wbd-pro-feature-icon">✨</span>
								<span>
									<strong>{__('Multi-Row Carousel', 'wbd-logo-carousel')}</strong> — Multiple rows with alternating scroll directions (row 1 left, row 2 right, row 3 left)
								</span>
							</li>
							<li>
								<span className="wbd-pro-feature-icon">✨</span>
								<span>
									<strong>{__('Grid Layout', 'wbd-logo-carousel')}</strong> — Display logos in a responsive grid (2–6 columns)
								</span>
							</li>
							<li>
								<span className="wbd-pro-feature-icon">✨</span>
								<span>
									<strong>{__('Masonry Layout', 'wbd-logo-carousel')}</strong> — Pinterest-style masonry grid (2–5 columns)
								</span>
							</li>
							<li>
								<span className="wbd-pro-feature-icon">✨</span>
								<span>
									<strong>{__('Priority Support', 'wbd-logo-carousel')}</strong> — Dedicated help within 24 hours
								</span>
							</li>
						</ul>
						<Button
							isPrimary
							className="wbd-pro-upgrade-btn"
							onClick={() => window.open(upgradeLink, '_blank')}
						>
							{__('Upgrade to Pro', 'wbd-logo-carousel')}
						</Button>
						<Button
							className="wbd-pro-demo-btn"
							onClick={() => window.open(demoLink, '_blank')}
						>
							{__('View Live Demo', 'wbd-logo-carousel')}
						</Button>
					</div>
				</PanelBody>

			</InspectorControls>

			{/* LIVE PREVIEW */}
			<div {...blockProps}>
				{renderCarouselPreview()}
			</div>
		</>
	);
}